import Vue from 'vue';
import Component from 'vue-class-component';
import SearchApproverComponent from './search-approver/search-approver.vue';
import SearchApproverComponentTs from './search-approver/search-approver';
import { DEPARTMENT_GROUP_NAMES } from '~/components/in-year-budget-requests/constants/department-group-names';
import { Prop, Watch } from 'vue-property-decorator';
import { ROWS } from '~/components/in-year-budget-requests/constants/rows';
import { MasterService } from '~/services/master-service';
import { Department, DepartmentGroups, SelectedStakeholder, Stakeholder } from '~/models/services/minifd';
import { EventBus } from '~/utils';
import { CompensationTableTotals } from '../iybr-compensation-table/iybr-compensation-table';
import { BudgetRequestPlanningStatus } from '~/utils/interfaces';
import { CompensationRequiredTotals } from '../create-iybr-form-model';
import {
    isHydroOrSteamUnder200kTable3,
    loadDepartmentsForGroup,
    resolveDepartmentForGroup
} from '~/components/in-year-budget-requests/utils/department-resolver';
import { Amount } from '~/components/in-year-budget-requests/constants/amounts';
import { formatToDecimals } from '~/utils/number-helper';
import { isNewProjectFlow } from '~/components/in-year-budget-requests/utils/request-type-groups';

const FORM_APPROVAL_WORKFLOW_REFS = {
    PLANT_MANAGER_REF: 'plantManager',
    PRIMARY_DEPARTMENT_REF: 'primaryDepartment',
    ASSET_STRATEGY_REF: 'assetStrategy',
    ASSET_OPERATIONS_REF: 'assetOperations',
    ASSET_MANAGEMENT_REF: 'assetManagement',
    BUSINESS_CONTROLLING_REF: 'businessControlling'
} as const;

@Component({
    components: {
        searchApprover: SearchApproverComponent,
    },
})
export default class FormApprovalWorkflow extends Vue {
    @Prop()
    totalBudgetRequired: number;

    @Prop()
    selectedLocalCompensation: string;

    @Prop()
    aeroClassification?: string;

    @Prop()
    isAssetStrategyRequired: boolean;

    @Prop()
    isHydroTechnology: boolean;

    @Prop({ default: false })
    isSteamTechnology: boolean;

    @Prop()
    isSwedenCountry: boolean;

    @Prop()
    primaryDepartmentName?: string;

    @Prop()
    compensationTotals?: CompensationTableTotals;

    @Prop()
    compensationRequired?: CompensationRequiredTotals;

    @Prop()
    requestType?: string

    @Prop()
    budgetInMtp: number

    @Prop()
    isFullyCompensated?: boolean

    @Prop({ default: 0 })
    budgetToBeApprovedMtpPlus1Eur: number

    @Prop({ default: 0 })
    budgetToBeApprovedEur: number

    $refs: {
        [FORM_APPROVAL_WORKFLOW_REFS.PLANT_MANAGER_REF]: SearchApproverComponentTs;
        [FORM_APPROVAL_WORKFLOW_REFS.PRIMARY_DEPARTMENT_REF]: SearchApproverComponentTs;
        [FORM_APPROVAL_WORKFLOW_REFS.ASSET_STRATEGY_REF]: SearchApproverComponentTs;
        [FORM_APPROVAL_WORKFLOW_REFS.ASSET_OPERATIONS_REF]: SearchApproverComponentTs;
        [FORM_APPROVAL_WORKFLOW_REFS.ASSET_MANAGEMENT_REF]: SearchApproverComponentTs;
        [FORM_APPROVAL_WORKFLOW_REFS.BUSINESS_CONTROLLING_REF]: SearchApproverComponentTs;
    };
    private readonly formLayout = ROWS;
    private readonly departmentGroupNames = DEPARTMENT_GROUP_NAMES;
    /** UI labels — API `DepartmentGroups.name` stays on `departmentGroupNames.*` for matching. */
    private readonly plantManagerApproverLabel = 'Plant / River Group Manager';
    private readonly assetManagementApproverLabel = 'Asset Management';
    private readonly FORM_APPROVAL_WORKFLOW_REFS = FORM_APPROVAL_WORKFLOW_REFS;
    private readonly MINI_FD_SERVICE = MasterService.instance.miniFdService;
    private departmentGroups: DepartmentGroups[] = null;
    private departmentGroupsLoadPromise: Promise<void> | null = null;
    private isAssetOperationsRequired: boolean = false;
    private isAssetManagementRequired: boolean = false;
    private plantManagerDepartment: Department = null;
    private assetStrategyDepartment: Department = null;
    private assetOperationsDepartment: Department = null;
    private assetManagementDepartment: Department = null;
    private businessControllingDepartment: Department = null;
    private pendingStakeholders: Stakeholder[] | null = null;

    private get delta(): number {
        return this.totalBudgetRequired - this.budgetInMtp
    }

    private get isFullyCompensatedProject(): boolean {
        if (this.isNotReleasedRequest) {
            return true;
        }

        const firstYearCompensation = this.compensationTotals?.mtpYearPlus1Total ?? 0;
        const firstYearRequired = this.compensationRequired?.mtpPlus1Required ?? 0;
        const compensationCoversRequired =
            formatToDecimals(firstYearCompensation, 3) > 0 &&
            formatToDecimals(firstYearCompensation, 3) >= formatToDecimals(firstYearRequired, 3);
        return this.isFullyCompensated || (
            !!this.compensationTotals?.hasCompensationEntries &&
            compensationCoversRequired
        );
    }

    private get isCompensationAvailable(): boolean {
        return this.isFullyCompensatedProject;
    }

    private get isBudgetOverrun(): boolean {
        return this.requestType === BudgetRequestPlanningStatus.BUDGET_OVERRUN.toString()
    }

    private get isNotReleasedRequest(): boolean {
        return this.requestType === BudgetRequestPlanningStatus.FLAGGED.toString();
    }

    private get isTable3Flow(): boolean {
        return (
            this.requestType === BudgetRequestPlanningStatus.BUDGET_OVERRUN.toString() ||
            isNewProjectFlow(this.requestType) ||
            this.requestType === BudgetRequestPlanningStatus.ADVANCE_RELEASE.toString()
        ) && !this.isCompensationAvailable
    }

    /** EUR basis for €200k / €2M approver bands — overall “budget to be approved” row total. */
    private get totalCost(): number {
        if (this.budgetToBeApprovedEur > 0) {
            return this.budgetToBeApprovedEur;
        }
        if (this.isBudgetOverrun) {
            return this.delta;
        }
        return this.totalBudgetRequired;
    }

    private get isHydroOrSteamUnder200kTable3(): boolean {
        return isHydroOrSteamUnder200kTable3({
            isTable3Flow: this.isTable3Flow,
            totalCost: this.totalCost,
            isHydroTechnology: this.isHydroTechnology,
            isSteamTechnology: this.isSteamTechnology
        });
    }

    private get isPlantManagerRequired(): boolean {
        if (this.isHydroTechnology && this.isSwedenCountry) {
            return this.isHydroOrSteamUnder200kTable3;
        }
        return true;
    }

    private get shouldDisplayDepartmentsNotSetNotification(): boolean {
        return (
            (this.isPlantManagerRequired && !this.plantManagerDepartment) ||
            !this.primaryDepartment ||
            (this.isAssetStrategyRequired && !this.assetStrategyDepartment) ||
            (this.isAssetOperationsRequired && !this.assetOperationsDepartment) ||
            (this.isAssetManagementRequired && !this.assetManagementDepartment) ||
            !this.businessControllingDepartment
        );
    }

    public getStakeholderRefs(): SearchApproverComponentTs[] {
        const stakeholderRefs: SearchApproverComponentTs[] = [];

        const primaryDepartmentStakeHolder = this.$refs[FORM_APPROVAL_WORKFLOW_REFS.PRIMARY_DEPARTMENT_REF];
        const businessControllingStakeholder = this.$refs[FORM_APPROVAL_WORKFLOW_REFS.BUSINESS_CONTROLLING_REF];

        stakeholderRefs.push(primaryDepartmentStakeHolder, businessControllingStakeholder)

        if (this.isPlantManagerRequired) {
            stakeholderRefs.push(this.$refs[FORM_APPROVAL_WORKFLOW_REFS.PLANT_MANAGER_REF]);
        }

        if (this.isAssetOperationsRequired) {
            stakeholderRefs.push(this.$refs[FORM_APPROVAL_WORKFLOW_REFS.ASSET_OPERATIONS_REF]);
        }

        if (this.isAssetStrategyRequired) {
            stakeholderRefs.push(this.$refs[FORM_APPROVAL_WORKFLOW_REFS.ASSET_STRATEGY_REF]);
        }

        if (this.isAssetManagementRequired) {
            stakeholderRefs.push(this.$refs[FORM_APPROVAL_WORKFLOW_REFS.ASSET_MANAGEMENT_REF]);
        }

        return stakeholderRefs;
    }

    public getStakeHolders(): SelectedStakeholder[] {
        return this.getStakeholderRefs().map(ref => ref.getSelectedUser());
    }

    public validateApprovalWorkflowForm(): boolean {
        // Return true if no stakeholder is falsy
        if (!this.getStakeHolders().some(stakeholder => !stakeholder)) {
            return true;
        }

        this.getStakeholderRefs()
            .filter(ref => !ref.getSelectedUser())
            .forEach(ref => ref.highlightInvalidUserInput());

        return false;
    }

    public populateApprovalWorkflow(stakeholders: Stakeholder[]): void {
        this.pendingStakeholders = stakeholders;
        this.applyPendingStakeholders();
    }

    private applyPendingStakeholders(): void {
        if (!this.pendingStakeholders?.length) {
            return;
        }

        const stakeholders = this.pendingStakeholders;
        const primaryDepartmentStakeholder = stakeholders.find((stakeholder) =>
            stakeholder.departmentGroupName === DEPARTMENT_GROUP_NAMES.PRIMARY_DEPARTMENT);
        if (primaryDepartmentStakeholder) {
            this.$refs[FORM_APPROVAL_WORKFLOW_REFS.PRIMARY_DEPARTMENT_REF].populateStakeholderData(primaryDepartmentStakeholder);
        }

        const assetManagementStakeholder = stakeholders.find((stakeholder) =>
            stakeholder.departmentGroupName === DEPARTMENT_GROUP_NAMES.ASSET_MANAGEMENT);
        if (assetManagementStakeholder) {
            this.$refs[FORM_APPROVAL_WORKFLOW_REFS.ASSET_MANAGEMENT_REF].populateStakeholderData(assetManagementStakeholder);
        }

        const businessControllingStakeholder = stakeholders.find((stakeholder) =>
            stakeholder.departmentGroupName === DEPARTMENT_GROUP_NAMES.BUSINESS_CONTROLLING);
        if (businessControllingStakeholder) {
            this.$refs[FORM_APPROVAL_WORKFLOW_REFS.BUSINESS_CONTROLLING_REF].populateStakeholderData(businessControllingStakeholder);
        }

        const plantManagerStakeholder = stakeholders.find((stakeholder) =>
            stakeholder.departmentGroupName === DEPARTMENT_GROUP_NAMES.PLANT_MANAGER);
        if (plantManagerStakeholder) {
            this.$refs[FORM_APPROVAL_WORKFLOW_REFS.PLANT_MANAGER_REF].populateStakeholderData(plantManagerStakeholder);
        }

        const assetStrategyStakeholder = stakeholders.find((stakeholder) =>
            stakeholder.departmentGroupName === DEPARTMENT_GROUP_NAMES.ASSET_STRATEGY);
        if (assetStrategyStakeholder) {
            this.$refs[FORM_APPROVAL_WORKFLOW_REFS.ASSET_STRATEGY_REF].populateStakeholderData(assetStrategyStakeholder);
        }

        const assetOperationsStakeholder = stakeholders.find((stakeholder) =>
            stakeholder.departmentGroupName === DEPARTMENT_GROUP_NAMES.ASSET_OPERATIONS);
        if (assetOperationsStakeholder) {
            this.$refs[FORM_APPROVAL_WORKFLOW_REFS.ASSET_OPERATIONS_REF].populateStakeholderData(assetOperationsStakeholder);
        }
    }

    @Watch('totalCost', { immediate: true, deep: true })
    private async onTotalCostChange(): Promise<void> {
        this.setDepartments();
    }

    @Watch('compensationTotals', { immediate: true, deep: true })
    private async onCompensationTotalsChange(): Promise<void> {
        this.setDepartments();
    }

    @Watch('compensationRequired', { immediate: true, deep: true })
    private async onCompensationRequiredChange(): Promise<void> {
        this.setDepartments();
    }

    @Watch('isFullyCompensated', { immediate: true, deep: true })
    private async onIsFullyCompensatedChange(): Promise<void> {
        this.setDepartments();
    }

    @Watch('budgetToBeApprovedEur', { immediate: true })
    @Watch('budgetToBeApprovedMtpPlus1Eur', { immediate: true, deep: true })
    @Watch('budgetInMtp', { immediate: true, deep: true })
    @Watch('requestType', { immediate: true })
    private async onBudgetToBeApprovedMtpPlus1EurChange(): Promise<void> {
        this.setDepartments();
    }

    private async setDepartments(): Promise<void> {
        if (!this.departmentGroups) {
            if (!this.departmentGroupsLoadPromise) {
                this.departmentGroupsLoadPromise = this.MINI_FD_SERVICE.getDepartments()
                    .then((groups) => {
                        this.departmentGroups = groups;
                        EventBus.$emit(EventBus.CHANGE_REQUEST.GET_DEPARTMENTS_ID, this.departmentGroups);
                    })
                    .then(
                        () => { this.departmentGroupsLoadPromise = null; },
                        (err) => {
                            this.departmentGroupsLoadPromise = null;
                            return Promise.reject(err);
                        }
                    );
            }
            await this.departmentGroupsLoadPromise;
        }

        this.resolveAllDepartments();
        this.syncAssetOperationsAndManagementRows();
        this.applyPendingStakeholders();
    }

    private get primaryDepartment(): Department {
        const primaryDepartments = loadDepartmentsForGroup(
            this.departmentGroups,
            this.departmentGroupNames.PRIMARY_DEPARTMENT,
            this.isTable3Flow
        );
        return primaryDepartments?.find((primaryDepartment) => primaryDepartment.name === this.primaryDepartmentName);
    }

    @Watch('selectedLocalCompensation', { immediate: true, deep: true })
    private async onSelectedLocalCompensationChange(): Promise<void> {
        // Compensation totals update after the parent tick; defer so isCompensationAvailable matches the new plan.
        await this.$nextTick();
        return this.setDepartments();
    }

    private syncAssetOperationsAndManagementRows(): void {
        const isOverTwoMillion = this.totalCost > Amount.TWO_MILLION;
        const isOverTwoMillionNoCompensation = !this.isFullyCompensatedProject && isOverTwoMillion;
        const isInTwoHundredKToTwoMillionBand =
            this.totalCost > Amount.TWO_HUNDRED_THOUSAND &&
            this.totalCost <= Amount.TWO_MILLION;

        // Energy Asset (Technology): hydro/steam Table 3 ≤€200k; uncompensated €200k–€2M; fully compensated >€2M (Technology Director).
        // Uncompensated >€2M: COO via Asset Management only — no Energy Asset row.
        if (this.isHydroOrSteamUnder200kTable3) {
            this.isAssetOperationsRequired = true;
        } else if (this.isFullyCompensatedProject) {
            this.isAssetOperationsRequired = isOverTwoMillion;
        } else {
            this.isAssetOperationsRequired = isInTwoHundredKToTwoMillionBand;
        }

        if (this.isFullyCompensatedProject && !isOverTwoMillion) {
            this.isAssetManagementRequired = this.totalCost > Amount.TWO_HUNDRED_THOUSAND;
        } else if (this.isHydroOrSteamUnder200kTable3) {
            // Table 3 hydro/steam ≤ €200k: four roles — Plant Manager, Primary, Technology Director/Manager, BC.
            this.isAssetManagementRequired = false;
        } else {
            this.isAssetManagementRequired = !(
                this.totalCost <= Amount.TWO_HUNDRED_THOUSAND && this.isFullyCompensatedProject
            ) || isOverTwoMillionNoCompensation;
        }

        if (this.isAssetOperationsRequired) {
            this.assetOperationsDepartment = this.resolveDepartment(this.departmentGroupNames.ASSET_OPERATIONS);
        } else {
            this.assetOperationsDepartment = null;
        }

        if (this.isAssetManagementRequired) {
            this.assetManagementDepartment = this.resolveDepartment(this.departmentGroupNames.ASSET_MANAGEMENT);
        } else {
            this.assetManagementDepartment = null;
        }
    }

    @Watch('isHydroTechnology')
    @Watch('isSteamTechnology')
    private addAssetOperationsToWorkflow(): void {
        void this.setDepartments();
    }

    private resolveAllDepartments(): void {
        this.plantManagerDepartment = this.resolveDepartment(this.departmentGroupNames.PLANT_MANAGER);
        this.assetStrategyDepartment = this.resolveDepartment(this.departmentGroupNames.ASSET_STRATEGY);
        this.assetOperationsDepartment = this.resolveDepartment(this.departmentGroupNames.ASSET_OPERATIONS);
        this.assetManagementDepartment = this.resolveDepartment(this.departmentGroupNames.ASSET_MANAGEMENT);
        this.businessControllingDepartment = this.resolveDepartment(this.departmentGroupNames.BUSINESS_CONTROLLING);
    }

    private resolveDepartment(groupName: string): Department {
        return resolveDepartmentForGroup({
            departmentGroups: this.departmentGroups,
            groupName,
            totalCost: this.totalCost,
            isTable3Flow: this.isTable3Flow,
            isSwedenCountry: this.isSwedenCountry,
            isHydroTechnology: this.isHydroTechnology,
            isSteamTechnology: this.isSteamTechnology,
            isCompensated: this.isFullyCompensatedProject
        });
    }
}
